import { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";

import { format } from "date-fns";

import { StaffTicketDetail, TicketSource } from "@signalist/shared-types";

import { TicketCategoryPill } from "@/pages/Private/pages/Support/components/TicketPills";
import { TICKET_DATE_FORMAT } from "@/pages/Private/pages/Support/pages/TicketDetail/components/TicketMessage";
import { ticketLinkedEntityKey } from "@/pages/Private/pages/Support/utils/ticketRowMeta";

import {
	StaffTicketStatusPill,
	TicketPriorityBadge,
	TicketSourceEmailBadge,
} from "../../../components/StaffTicketPills";
import { ticketSenderName } from "../../../utils/staffTicketRowMeta";

/**
 * The thread column's head (mockup `.thead-d`): subject with the staff status pill and the
 * priority, then category · company · filer · opened via · related entity · page pattern.
 */
export const StaffTicketDetailHeader: FunctionComponent<{ ticket: StaffTicketDetail }> = ({
	ticket,
}) => {
	const { t } = useTranslation();
	const linked = ticketLinkedEntityKey(ticket);

	return (
		<div className="flex flex-col gap-2 border-b border-border pb-4">
			<div className="flex flex-wrap items-center gap-3">
				<h2 className="min-w-0 flex-1 text-base font-semibold text-text">{ticket.subject}</h2>
				<StaffTicketStatusPill status={ticket.status} />
				<TicketPriorityBadge priority={ticket.priority} />
				{ticket.source === TicketSource.EMAIL && <TicketSourceEmailBadge />}
			</div>
			<div className="flex flex-wrap items-center gap-2 text-xs text-text-meta">
				<TicketCategoryPill category={ticket.category} />
				<span>·</span>
				<span>
					<span className="font-semibold text-text">{ticket.companyName ?? "—"}</span>
					{` · ${ticketSenderName(ticket)}`}
				</span>
				<span>·</span>
				<span>
					{t("support.staff.detail.openedVia", {
						date: format(new Date(ticket.createdAt), TICKET_DATE_FORMAT),
						source: t(`support.staff.detail.sourceShort.${ticket.source}`),
					})}
				</span>
				{linked && (
					<>
						<span>·</span>
						<span>
							{t("support.staff.detail.related", {
								entity: t(`support.linkedEntity.${linked.key}`, { id: linked.id }),
							})}
						</span>
					</>
				)}
				{ticket.routePattern && (
					<>
						<span>·</span>
						<span className="inline-flex items-center gap-1">
							{t("support.staff.detail.page")}
							<code className="rounded bg-status-info-light px-1 py-0.5 text-[11px] text-text">
								{ticket.routePattern}
							</code>
						</span>
					</>
				)}
			</div>
		</div>
	);
};
